/**
 * Utility helpers for mutations, including:
 * - Automatic refetch query configuration by resource
 * - Shared toast notifications for common mutation scenarios
 * This keeps entity flows consistent and reduces duplication across pages.
 */
import { toast } from 'sonner';

export interface RefetchConfig {
	/** The main list query for the entity */
	listQuery: string;
	/** Additional related queries that should be refetched */
	relatedQueries?: string[];
	/** Human readable label used for notifications */
	entityLabel?: string;
}

/**
 * Mapping of resource types to their refetch configurations
 */
export const REFETCH_CONFIGS: Record<string, RefetchConfig> = {
        'pricing-policies': {
                listQuery: 'PricingPolicies',
                relatedQueries: ['Scenarios', 'BillingCycles'],
                entityLabel: 'Pricing Policy'
        },
        'limit-policies': {
                listQuery: 'LimitPolicies',
                relatedQueries: ['Scenarios', 'BillingCycles'],
                entityLabel: 'Limit Policy'
        },
        scenarios: {
                listQuery: 'Scenarios',
                relatedQueries: [
			'VehicleTypes',
			'CustomerCategories',
			'WeeklyDateSets',
			'Conditions'
		],
		entityLabel: 'Scenario'
	},
	products: {
		listQuery: 'Products',
		entityLabel: 'Product'
	},
        users: {
                listQuery: 'Users',
                entityLabel: 'User'
        },
        customers: {
                listQuery: 'Customers',
                entityLabel: 'Customer'
        },
        campaigns: {
                listQuery: 'Campaigns',
                entityLabel: 'Campaign'
        },
	vouchers: {
		listQuery: 'Vouchers',
		entityLabel: 'Voucher'
	},
	vehicles: {
		listQuery: 'Vehicles',
		entityLabel: 'Vehicle'
	},
	gates: {
		listQuery: 'Gates',
		entityLabel: 'Gate'
	},
	contracts: {
		listQuery: 'ContractsList',
		relatedQueries: ['Customers', 'Vehicles'],
		entityLabel: 'Contract'
	},
	parkingSessions: {
		listQuery: 'ParkingSessionsList',
		relatedQueries: ['Vehicles'],
		entityLabel: 'Parking Session'
	},
	tariffs: {
		listQuery: 'Tariffs',
		entityLabel: 'Tariff'
	}
};

/**
 * Get refetch queries for a specific resource type
 * @param resourceType The resource type (e.g., 'rates', 'pricing-policies')
 * @param includeRelated Whether to include related queries (default: true)
 * @returns Array of query names to refetch
 */
export const getRefetchQueries = (
	resourceType: string,
	includeRelated: boolean = true
): string[] => {
	const config = REFETCH_CONFIGS[resourceType];

	if (!config) {
		console.warn(
			`No refetch configuration found for resource type: ${resourceType}`
		);
		return [];
	}

	const queries = [config.listQuery];

	if (includeRelated && config.relatedQueries) {
		queries.push(...config.relatedQueries);
	}

	return queries;
};

/**
 * Generic mutation options with automatic refetch configuration
 * @param resourceType The resource type being mutated
 * @param variables The mutation variables
 * @param options Additional options
 * @returns Mutation options with refetchQueries configured
 */
export const getMutationOptions = <TVariables = any>(
	resourceType: string,
	variables: TVariables,
	options: {
		includeRelated?: boolean;
		additionalRefetch?: string[];
		onCompleted?: (data: any) => void;
		onError?: (error: any) => void;
	} = {}
) => {
	const {
		includeRelated = true,
		additionalRefetch = [],
		onCompleted,
		onError
	} = options;

	const refetchQueries = [
		...getRefetchQueries(resourceType, includeRelated),
		...additionalRefetch
	];

	return {
		variables,
		refetchQueries,
		onCompleted,
		onError
	};
};

/**
 * Hook to get mutation options with automatic refetch for a resource
 * @param resourceType The resource type
 * @returns Function to generate mutation options
 */
export const useMutationRefetch = (resourceType: string) => {
	return (
		variables: Record<string, any>,
		options: Parameters<typeof getMutationOptions>[2] = {}
	) => getMutationOptions(resourceType, variables, options);
};

const humaniseResourceType = (resourceType: string) => {
	return resourceType
		.split(/[-_]/g)
		.filter(Boolean)
		.map(part => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ');
};

/**
 * Resolve a human readable label for a resource.
 */
export const getResourceLabel = (resourceType: string): string => {
	const config = REFETCH_CONFIGS[resourceType];

	if (config?.entityLabel) {
		return config.entityLabel;
	}

	return humaniseResourceType(resourceType);
};

const successActionLabels: Record<'create' | 'update' | 'delete', string> = {
	create: 'created successfully.',
	update: 'updated successfully.',
	delete: 'deleted successfully.'
};

const errorActionLabels: Record<'create' | 'update' | 'delete', string> = {
	create: 'Failed to create',
	update: 'Failed to update',
	delete: 'Failed to delete'
};

/**
 * Show a standard success toast for entity mutations to keep UX consistent.
 */
export const showMutationSuccessToast = (
	resourceType: string,
	action: keyof typeof successActionLabels,
	options: { message?: string } = {}
) => {
	const entityLabel = getResourceLabel(resourceType);
	const defaultMessage = `${entityLabel} ${successActionLabels[action]}`;

	toast.success(options.message ?? defaultMessage);
};

/**
 * Show a standard error toast for entity mutation failures to keep UX consistent.
 * @param resourceType The resource type (e.g., 'rates', 'pricing-policies')
 * @param action The mutation action that failed
 * @param options Optional configuration including custom error message
 */
export const showMutationErrorToast = (
	resourceType: string,
	action: keyof typeof errorActionLabels,
	options: { message?: string; error?: unknown } = {}
) => {
	const entityLabel = getResourceLabel(resourceType);
	const actionLabel = errorActionLabels[action];

	// Extract error message from error object if provided
	let errorDetail = options.message;
	if (!errorDetail && options.error) {
		errorDetail =
			options.error instanceof Error
				? options.error.message
				: 'An unexpected error occurred.';
	}

	const title = `${actionLabel} ${entityLabel}`;
	const description = errorDetail || 'Please try again.';

	toast.error(title, { description });
};
